import React from "react";
import Brand from "./Brand";
import SiteFooter, { CONTACT_EMAIL } from "./SiteFooter";

type LegalPageKind = "impressum" | "datenschutz" | "avv" | "agb";

type LegalPageProps = {
  page: LegalPageKind;
};

/**
 * Title, canonical path and body for each legal page, in one place.
 *
 * The canonical path is the spelling we want indexed, not necessarily the one
 * that was requested: `/Datenschutz` and `/datenschutz` are both served, and
 * so are `/vermietungen/AVV` and `/vermietungen/agb` in either case.
 *
 * `noindex` keeps a page out of search results. It is set on the AVV, which is
 * a contract document for customers rather than something anyone should reach
 * from a search, and which is deliberately not linked from the footer either.
 */
const PAGES: Record<LegalPageKind, { title: string; path: string; noindex?: boolean }> = {
  impressum: { title: "Impressum", path: "/impressum" },
  datenschutz: { title: "Datenschutzerklärung", path: "/Datenschutz" },
  avv: { title: "Auftragsverarbeitungsvertrag", path: "/vermietungen/AVV", noindex: true },
  agb: { title: "Allgemeine Geschäftsbedingungen", path: "/vermietungen/agb" },
};

function LegalHeader() {
  return (
    <header className="legal-header">
      <div className="nav-shell">
        <Brand href="/" productLabel="Bußgeldmanagement" />
        <a className="nav-cta" href="/">Zur Startseite</a>
      </div>
    </header>
  );
}

function Impressum() {
  return (
    <article className="legal-page section-shell">
      <p className="eyebrow">Rechtliche Informationen</p>
      <h1>Impressum</h1>
      <p className="legal-intro">Angaben gemäß § 5 Digitale-Dienste-Gesetz (DDG)</p>

      <section className="legal-section">
        <h2>Anbieter</h2>
        <address>
          <strong>Martin Digital Solutions</strong>
          <span>Inhaber: Julian Martin</span>
          <span>Cranachstr. 44</span>
          <span>12157 Berlin</span>
          <span>Deutschland</span>
        </address>
      </section>

      <section className="legal-section legal-facts">
        <h2>Steuerangaben</h2>
        <p><strong>Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG:</strong> DE464477417</p>
        <p>Kleinunternehmer gemäß § 19 UStG. Wir weisen keine Umsatzsteuer aus.</p>
      </section>

      <section className="legal-section legal-facts">
        <h2>Kontakt</h2>
        <p><strong>E-Mail:</strong> <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
        <p><strong>Telefon:</strong> <a href="tel:+4915678306360">015678 306360</a></p>
      </section>

      {/* Two sections are deliberately absent, and both are on most other
          Impressen - do not add them back without the condition changing.
          "Verantwortlich nach § 18 Abs. 2 MStV" applies only to a
          journalistisch-redaktionell gestaltetes Angebot; this site has four
          routes and no editorial content. And § 36 VSBG binds only businesses
          with more than 10 employees dealing with Verbrauchern, while AGB § 1
          restricts Gatai to Unternehmer i.S.d. § 14 BGB - a placeholder saying
          the statement "folgt in Kürze" is worse than no section at all. The EU
          ODR platform that such a section used to link was shut down on
          2025-07-20, so that link must not come back either. */}
    </article>
  );
}

function Datenschutz() {
  return (
    <article className="legal-page section-shell">
      <p className="eyebrow">Informationen nach Art. 13 DSGVO</p>
      <h1 aria-label="Datenschutzerklärung">Datenschutz<wbr/>erklärung</h1>
      <p className="legal-intro">Diese Erklärung informiert darüber, wie personenbezogene Daten beim Besuch dieser Website und bei einer Kontaktaufnahme verarbeitet werden.</p>

      <section className="legal-section">
        <h2>1. Verantwortlicher</h2>
        <address>
          <strong>Martin Digital Solutions</strong>
          <span>Inhaber: Julian Martin</span>
          <span>Cranachstr. 44</span>
          <span>12157 Berlin</span>
          <span>Deutschland</span>
          <span>E-Mail: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></span>
        </address>
        {/* Deliberately not a "Datenschutzbeauftragter": the duty under Art. 37
            DSGVO / § 38 BDSG does not apply here, and the Inhaber could not hold
            the role himself anyway (Art. 38 Abs. 6 DSGVO, conflict of interest).
            The AVV states the same thing - keep the two in step. */}
        <p><strong>Ansprechpartner für den Datenschutz:</strong> Julian Martin, <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Eine Pflicht zur Benennung einer/eines Datenschutzbeauftragten nach Art. 37 DSGVO, § 38 BDSG besteht derzeit nicht.</p>
      </section>

      <section className="legal-section">
        <h2>2. Aufruf der Website und Server-Protokolle</h2>
        <p>Beim erstmaligen Abruf unserer Startseite verarbeiten wir die IP-Adresse, Datum und Uhrzeit des Zugriffs, den aufgerufenen Pfad, die Referrer-URL, den Browsertyp sowie die Browsersprache. Rechtliche Unterseiten, statische Dateien, API-Aufrufe und Navigation innerhalb einer bereits geladenen Seite werden dabei nicht erfasst.</p>
        <p>Die Verarbeitung dient der sicheren und stabilen Bereitstellung der Website, der Erkennung und Abwehr technischer Angriffe sowie einer einfachen, serverseitigen Reichweitenmessung. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Unser berechtigtes Interesse liegt im sicheren und funktionsfähigen Betrieb sowie in der bedarfsgerechten Verbesserung dieses Online-Angebots.</p>
        <p><strong>Hostinganbieter:</strong> Uberspace, Jonas Pasche, Kaiserstr. 15, 55116 Mainz<br/><strong>Speicherdauer:</strong> Die erfassten Daten werden nach sieben Tagen gelöscht.</p>
      </section>

      <section className="legal-section">
        <h2>3. Kontaktaufnahme und Demo-Anfrage</h2>
        <p>Wenn Sie das Kontaktformular nutzen oder uns per E-Mail kontaktieren, verarbeiten wir die von Ihnen übermittelten Angaben. Dazu gehören Name, E-Mail-Adresse oder Telefonnummer, Nachrichteninhalt sowie die für die Bearbeitung erforderlichen technischen Metadaten.</p>
        <p>Die Daten werden verwendet, um Ihre Anfrage zu beantworten, eine Produktdemo vorzubereiten und gegebenenfalls vorvertragliche Maßnahmen durchzuführen. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO, soweit Ihre Anfrage auf einen Vertragsschluss gerichtet ist. In anderen Fällen erfolgt die Verarbeitung auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO; unser berechtigtes Interesse besteht in der Bearbeitung geschäftlicher Anfragen.</p>
        <p>Pflichtangaben sind im Formular gekennzeichnet. Ohne diese Angaben können wir Ihre Anfrage nicht bearbeiten. Die Angabe einer E-Mail-Adresse oder Telefonnummer ist erforderlich, damit wir antworten können.</p>
        <p><strong>Technischer E-Mail-Dienstleister:</strong> Uberspace, Jonas Pasche, Kaiserstr. 15, 55116 Mainz<br/><strong>Speicherdauer von Kontaktanfragen:</strong> Kontaktanfragen werden so lange gespeichert, wie dies für ihre Bearbeitung und die weitere geschäftliche Kommunikation erforderlich ist. Anschließend werden sie gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen.</p>
      </section>

      <section className="legal-section">
        <h2>4. Cookies, Reichweitenmessung und Marketing</h2>
        <p>Die in Abschnitt 2 beschriebene Reichweitenmessung erfolgt ausschließlich auf unserem Server. Wir setzen dafür keine Cookies, speichern keine dauerhaften Kennungen im Browser und binden keine Analyse-, Marketing- oder Social-Media-Trackingdienste Dritter ein.</p>
      </section>

      <section className="legal-section">
        <h2>5. Vertragsabwicklung und Zahlung</h2>
        <p>Für die Anbahnung und Durchführung eines Vertrages über die Nutzung von Gatai verarbeiten wir die Angaben unserer Kundinnen und Kunden sowie ihrer Ansprechpartnerinnen und Ansprechpartner: Firmenname, Anschrift, Steuernummer bzw. Umsatzsteuer-Identifikationsnummer, Name und E-Mail-Adresse des Ansprechpartners, Vertragsdaten sowie Rechnungs- und Zahlungsdaten. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO.</p>
        {/* Stripe belongs here and NOT in the AVV's Annex 3. Annex 3 lists
            sub-processors for data we process *on behalf of* a car rental -
            renters, fines, driver mails. Stripe sees none of that; it processes
            our own customer's billing data, and for that we are the controller
            rather than a processor. Putting it in Annex 3 would be wrong on the
            merits, not merely redundant. */}
        <p><strong>Zahlungsdienstleister:</strong> Stripe Payments Europe, Ltd., 1 Grand Canal Street Lower, Grand Canal Dock, Dublin, Irland. Stripe wickelt den Einzug per SEPA-Lastschrift ab und verarbeitet dazu Name, Anschrift, E-Mail-Adresse, Bankverbindung, Mandatsreferenz sowie die einzelnen Zahlungsvorgänge. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO. Einzelheiten zur Verarbeitung durch Stripe und zu etwaigen Übermittlungen in Drittländer ergeben sich aus der Datenschutzerklärung von Stripe.</p>
        <p><strong>Aufbewahrung:</strong> Rechnungs- und Buchungsunterlagen bewahren wir nach den handels- und steuerrechtlichen Vorgaben auf (§ 147 AO, § 257 HGB). Für diesen Zeitraum ist die Verarbeitung auf die Erfüllung dieser Pflichten eingeschränkt. Rechtsgrundlage ist Art. 6 Abs. 1 lit. c DSGVO.</p>
      </section>

      <section className="legal-section">
        <h2>6. Speicherdauer</h2>
        <p>Wir speichern personenbezogene Daten nur so lange, wie dies für den jeweiligen Zweck erforderlich ist oder gesetzliche Aufbewahrungspflichten bestehen. Konkrete Fristen für die oben genannten Verarbeitungsvorgänge folgen in Kürze.</p>
      </section>

      <section className="legal-section">
        <h2>7. Ihre Rechte</h2>
        <p>Sie haben nach Maßgabe der DSGVO insbesondere das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung und Datenübertragbarkeit. Soweit eine Verarbeitung auf Ihrer Einwilligung beruht, können Sie diese jederzeit mit Wirkung für die Zukunft widerrufen.</p>
        <p>Sie können einer Verarbeitung, die auf Art. 6 Abs. 1 lit. f DSGVO beruht, aus Gründen widersprechen, die sich aus Ihrer besonderen Situation ergeben. Zur Ausübung Ihrer Rechte genügt eine Nachricht an <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
      </section>

      <section className="legal-section">
        <h2>8. Beschwerderecht</h2>
        <p>Sie haben das Recht, sich bei einer Datenschutzaufsichtsbehörde zu beschweren. Für Unternehmen mit Sitz in Berlin ist insbesondere folgende Behörde erreichbar:</p>
        <address>
          <strong>Berliner Beauftragte für Datenschutz und Informationsfreiheit</strong>
          <span>Alt-Moabit 59–61</span>
          <span>10555 Berlin</span>
          <span>E-Mail: <a href="mailto:mailbox@datenschutz-berlin.de">mailbox@datenschutz-berlin.de</a></span>
        </address>
      </section>

      <section className="legal-section">
        <h2>9. Datensicherheit und automatisierte Entscheidungen</h2>
        <p>Wir treffen angemessene technische und organisatorische Maßnahmen, um personenbezogene Daten vor Verlust, Manipulation und unberechtigtem Zugriff zu schützen. Eine ausschließlich automatisierte Entscheidungsfindung einschließlich Profiling findet nicht statt.</p>
      </section>

      <section className="legal-section">
        <h2>10. Aktualität</h2>
        <p>Stand dieser Datenschutzerklärung: Juli 2026. Wir aktualisieren diese Erklärung, wenn sich die Website, die eingesetzten Dienste oder die rechtlichen Anforderungen ändern.</p>
      </section>
    </article>
  );
}

/** The processor, named identically in the Impressum and in every annex below. */
const PROCESSOR = {
  company: "Martin Digital Solutions",
  owner: "Julian Martin",
  street: "Cranachstr. 44",
  city: "12157 Berlin",
};

/**
 * Approved sub-processors, Annex 3.
 *
 * Anthropic is the only one outside the EU and the only one that sees a scanned
 * document, so it carries the transfer mechanism in its own row rather than in
 * a footnote.
 */
const SUBPROCESSORS: {
  name: string;
  seat: string;
  purpose: string;
  location: string;
}[] = [
  {
    name: "Uberspace (Jonas Pasche)",
    seat: "Kaiserstr. 15, 55116 Mainz",
    purpose: "Hosting der Anwendung und der Datenbank, Serverbetrieb, Sicherungen",
    location: "Deutschland",
  },
  {
    name: "Anthropic PBC",
    seat: "548 Market St, San Francisco, CA 94104, USA",
    purpose:
      "Automatisierte Auswertung der hochgeladenen Seitenbilder (Auslesen von Kennzeichen, Aktenzeichen, Tatzeitpunkt, Behörde, Beträgen und Fristen)",
    location:
      "USA, Übermittlung auf Grundlage der Standardvertragsklauseln der EU-Kommission nebst ergänzendem Datenverarbeitungszusatz",
  },
  {
    name: "Brevo GmbH",
    seat: "Köhlstr. 21-25, 50827 Köln",
    purpose:
      "Technischer Versand der freigegebenen E-Mails an die Mieterinnen und Mieter sowie Erfassung des Zustellstatus",
    location: "Europäische Union",
  },
];

/**
 * Auftragsverarbeitungsvertrag nach Art. 28 Abs. 3 DSGVO.
 *
 * Public rather than a PDF handed out per customer, because a controller has to
 * be able to read it before signing anything, and because Annex 3 changes
 * whenever a sub-processor does - a URL updates, a signed PDF in an inbox does
 * not.
 */
function Avv() {
  return (
    <article className="legal-page section-shell">
      <p className="eyebrow">Vereinbarung nach Art. 28 DSGVO</p>
      {/* Soft hyphens so the word breaks where German would break it rather than
          overflowing a phone. `aria-label` because they land in the accessible
          name otherwise, exactly as on the Datenschutz heading. */}
      <h1 aria-label="Auftragsverarbeitungsvertrag">Auftrags&shy;verarbeitungs&shy;vertrag</h1>
      <p className="legal-intro">
        Diese Vereinbarung regelt die Verarbeitung personenbezogener Daten, die Gatai im Auftrag
        einer Vermietung durchführt. Sie ist Bestandteil des Hauptvertrages über die Nutzung von
        Gatai und gilt für dessen gesamte Laufzeit.
      </p>

      <section className="legal-section">
        <h2>Vertragsparteien</h2>
        <p>
          <strong>Verantwortlicher</strong> im Sinne des Art. 4 Nr. 7 DSGVO ist das im Hauptvertrag
          bezeichnete Unternehmen, das Gatai einsetzt (nachfolgend „Verantwortlicher“).
        </p>
        <p>
          <strong>Auftragsverarbeiter</strong> im Sinne des Art. 4 Nr. 8 DSGVO ist:
        </p>
        <address>
          <strong>{PROCESSOR.company}</strong>
          <span>Inhaber: {PROCESSOR.owner}</span>
          <span>{PROCESSOR.street}</span>
          <span>{PROCESSOR.city}</span>
          <span>Deutschland</span>
          <span>
            E-Mail: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </span>
        </address>
        <p>
          Der Verantwortliche entscheidet allein über Zwecke und Mittel der Verarbeitung. Der
          Auftragsverarbeiter verarbeitet die Daten ausschließlich für ihn und nach seinen
          Weisungen.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 1 Gegenstand, Umfang und Dauer</h2>
        <p>
          Gegenstand des Auftrags ist die Bereitstellung und der Betrieb von Gatai, einer Anwendung
          für das Bußgeldmanagement von Vermietungen. Der Verantwortliche erfasst mit ihr
          Halterschreiben (Bußgeldbescheide, Anhörungsbögen, Zeugenfragebögen, Verwarnungsgelder),
          lässt deren Inhalt auslesen, ordnet ihnen den passenden Mietvorgang zu und erzeugt daraus
          einen E-Mail-Entwurf zur Fahrerbenennung, den er selbst freigibt.
        </p>
        <p>
          Art, Umfang, Zweck der Verarbeitung, die Art der personenbezogenen Daten und die
          Kategorien betroffener Personen ergeben sich abschließend aus <strong>Anlage 1</strong>.
        </p>
        <p>
          Die Vereinbarung beginnt mit dem Hauptvertrag und endet mit ihm. Sie kann vom
          Verantwortlichen jederzeit ohne Einhaltung einer Frist gekündigt werden, soweit der
          Hauptvertrag dadurch nicht undurchführbar wird.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 2 Weisungsrecht</h2>
        <p>
          Der Auftragsverarbeiter verarbeitet personenbezogene Daten ausschließlich auf
          dokumentierte Weisung des Verantwortlichen, einschließlich in Bezug auf die Übermittlung
          in Drittländer. Diese Vereinbarung nebst Anlagen sowie die Nutzung der Anwendung im
          vorgesehenen Funktionsumfang gelten als Weisung.
        </p>
        <p>
          Weisungen ergehen in Textform. Mündliche Weisungen sind unverzüglich in Textform zu
          bestätigen. Der Auftragsverarbeiter informiert den Verantwortlichen unverzüglich, wenn er
          der Auffassung ist, dass eine Weisung gegen die DSGVO oder andere Datenschutzvorschriften
          verstößt; er darf die Ausführung bis zur Bestätigung durch den Verantwortlichen
          aussetzen.
        </p>
        <p>
          Ist der Auftragsverarbeiter nach Unionsrecht oder dem Recht eines Mitgliedstaats zu einer
          Verarbeitung verpflichtet, teilt er dem Verantwortlichen die rechtlichen Anforderungen
          vor der Verarbeitung mit, sofern das betreffende Recht dies nicht wegen eines wichtigen
          öffentlichen Interesses verbietet.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 3 Pflichten des Auftragsverarbeiters</h2>
        <p>Der Auftragsverarbeiter</p>
        <ul className="legal-list">
          <li>
            verarbeitet die Daten nur zu den in Anlage 1 genannten Zwecken und nutzt sie nicht für
            eigene Zwecke, insbesondere nicht zum Training eigener oder fremder Modelle,
          </li>
          <li>
            trennt die Daten des Verantwortlichen technisch und organisatorisch von den Daten
            anderer Auftraggeber (Anlage 2),
          </li>
          <li>
            trifft die nach Art. 32 DSGVO erforderlichen technischen und organisatorischen
            Maßnahmen und passt sie an den Stand der Technik an, wobei das in Anlage 2 vereinbarte
            Schutzniveau nicht unterschritten wird,
          </li>
          <li>
            berichtigt, löscht oder schränkt die Verarbeitung von Daten nur auf Weisung des
            Verantwortlichen ein, soweit keine gesetzliche Pflicht besteht,
          </li>
          <li>
            unterstützt den Verantwortlichen bei der Erfüllung von Betroffenenrechten sowie bei
            Datenschutz-Folgenabschätzungen und vorherigen Konsultationen (§ 6),
          </li>
          <li>
            führt ein Verzeichnis aller Verarbeitungstätigkeiten im Auftrag nach Art. 30 Abs. 2
            DSGVO und stellt es dem Verantwortlichen auf Anforderung zur Verfügung.
          </li>
        </ul>
        <p>
          Ansprechpartner für den Datenschutz beim Auftragsverarbeiter ist{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Eine Pflicht zur Benennung eines
          Datenschutzbeauftragten nach Art. 37 DSGVO, § 38 BDSG besteht derzeit nicht.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 4 Vertraulichkeit</h2>
        <p>
          Der Auftragsverarbeiter setzt zur Verarbeitung nur Personen ein, die zur Vertraulichkeit
          verpflichtet und mit den einschlägigen Datenschutzvorschriften vertraut gemacht worden
          sind. Die Verpflichtung wirkt über das Ende der Tätigkeit hinaus fort.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 5 Technische und organisatorische Maßnahmen</h2>
        <p>
          Die zum Zeitpunkt des Vertragsschlusses getroffenen Maßnahmen sind in{" "}
          <strong>Anlage 2</strong> beschrieben. Der Auftragsverarbeiter überprüft sie regelmäßig
          und darf sie fortentwickeln, solange das vereinbarte Schutzniveau erhalten bleibt.
          Wesentliche Änderungen sind zu dokumentieren.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 6 Unterauftragsverarbeiter</h2>
        <p>
          Der Verantwortliche erteilt dem Auftragsverarbeiter die allgemeine Genehmigung, weitere
          Auftragsverarbeiter hinzuzuziehen. Die im Zeitpunkt des Vertragsschlusses eingesetzten
          Unterauftragsverarbeiter sind in <strong>Anlage 3</strong> abschließend aufgeführt und
          gelten als genehmigt.
        </p>
        <p>
          Der Auftragsverarbeiter informiert den Verantwortlichen mindestens vier Wochen vor der
          Beauftragung eines weiteren oder dem Wechsel eines bestehenden Unterauftragsverarbeiters
          in Textform. Der Verantwortliche kann innerhalb von zwei Wochen ab Zugang der Information
          aus einem wichtigen datenschutzrechtlichen Grund widersprechen. Widerspricht er, kann der
          Auftragsverarbeiter die betroffene Leistung ohne den Unterauftragsverarbeiter erbringen
          oder den Hauptvertrag mit angemessener Frist kündigen.
        </p>
        <p>
          Der Auftragsverarbeiter verpflichtet jeden Unterauftragsverarbeiter schriftlich oder in
          einem elektronischen Format auf Datenschutzpflichten, die denen dieser Vereinbarung
          entsprechen, und bleibt dem Verantwortlichen gegenüber für dessen Verhalten voll
          verantwortlich.
        </p>
        <p>
          Nebenleistungen, die der Auftragsverarbeiter bei Dritten in Anspruch nimmt, ohne dass
          diese auf personenbezogene Daten des Verantwortlichen zugreifen, sind keine
          Unterauftragsverarbeitung.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 7 Unterstützung des Verantwortlichen</h2>
        <p>
          Wendet sich eine betroffene Person unmittelbar an den Auftragsverarbeiter, leitet dieser
          das Anliegen unverzüglich an den Verantwortlichen weiter und beantwortet es nicht selbst.
        </p>
        <p>
          Der Auftragsverarbeiter unterstützt den Verantwortlichen mit geeigneten technischen und
          organisatorischen Maßnahmen dabei, Anträgen auf Auskunft, Berichtigung, Löschung,
          Einschränkung, Datenübertragbarkeit und Widerspruch nachzukommen, sowie bei der Einhaltung
          der Art. 32 bis 36 DSGVO. Auskunft, Berichtigung und Löschung einzelner Vorgänge sind
          über die Anwendung selbst möglich.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 8 Meldung von Datenschutzverletzungen</h2>
        <p>
          Der Auftragsverarbeiter meldet dem Verantwortlichen jede Verletzung des Schutzes
          personenbezogener Daten unverzüglich, spätestens innerhalb von 24 Stunden nach Kenntnis,
          in Textform an die im Hauptvertrag hinterlegte Adresse. Die Meldung beschreibt, soweit
          bekannt, die Art der Verletzung, die betroffenen Datenkategorien, die ungefähre Zahl der
          betroffenen Personen und Datensätze, die wahrscheinlichen Folgen sowie die ergriffenen
          oder vorgeschlagenen Gegenmaßnahmen.
        </p>
        <p>
          Die Meldepflichten nach Art. 33 und 34 DSGVO gegenüber Aufsichtsbehörde und betroffenen
          Personen bleiben Sache des Verantwortlichen. Der Auftragsverarbeiter unterstützt ihn
          dabei.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 9 Nachweise und Kontrollen</h2>
        <p>
          Der Auftragsverarbeiter weist die Einhaltung der Pflichten aus dieser Vereinbarung auf
          Anforderung nach, insbesondere durch Auskunft, eine aktuelle Beschreibung der Maßnahmen
          nach Anlage 2 oder geeignete Zertifikate und Prüfberichte seiner
          Unterauftragsverarbeiter.
        </p>
        <p>
          Reichen diese Nachweise nicht aus, kann der Verantwortliche nach rechtzeitiger Ankündigung
          zu üblichen Geschäftszeiten und ohne Störung des Betriebsablaufs eine Überprüfung
          durchführen oder durch einen zur Verschwiegenheit verpflichteten, nicht mit dem
          Auftragsverarbeiter im Wettbewerb stehenden Prüfer durchführen lassen, in der Regel
          höchstens einmal jährlich sowie anlassbezogen.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 10 Löschung und Rückgabe</h2>
        <p>
          Nach Beendigung des Auftrags löscht der Auftragsverarbeiter alle im Auftrag verarbeiteten
          personenbezogenen Daten oder gibt sie nach Wahl des Verantwortlichen zurück, spätestens
          innerhalb von 30 Tagen nach Vertragsende. Der Verantwortliche teilt seine Wahl vor
          Vertragsende mit; äußert er sich nicht, werden die Daten gelöscht.
        </p>
        <p>
          Ausgenommen sind Daten, für die eine gesetzliche Aufbewahrungspflicht besteht. Diese
          werden bis zum Ablauf der Frist eingeschränkt verarbeitet und danach gelöscht.
          Sicherungskopien werden im Rahmen der regulären Rotation überschrieben.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 11 Schlussbestimmungen</h2>
        <p>
          Änderungen und Ergänzungen bedürfen der Textform. Bei Widersprüchen zwischen dieser
          Vereinbarung und dem Hauptvertrag geht diese Vereinbarung vor, soweit die Verarbeitung
          personenbezogener Daten betroffen ist.
        </p>
        <p>
          Sollte eine Bestimmung unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen
          unberührt. Es gilt das Recht der Bundesrepublik Deutschland.
        </p>
      </section>

      <section className="legal-section">
        <h2>Anlage 1 – Beschreibung der Verarbeitung</h2>

        <h3>Zweck</h3>
        <p>
          Erfassung und Auswertung von Halterschreiben, Zuordnung des jeweiligen Mietvorgangs und
          Vorbereitung der Fahrerbenennung gegenüber der Bußgeldbehörde bzw. der Weiterleitung an
          die verantwortliche Mieterin oder den verantwortlichen Mieter, insbesondere im
          Zusammenhang mit § 25a StVG und § 31a StVZO.
        </p>

        <h3>Art der Verarbeitung</h3>
        <p>
          Erheben, Erfassen, Organisieren, Ordnen, Speichern, Anpassen, Auslesen, Abfragen,
          Verwenden, Offenlegen durch Übermittlung an die betroffene Mieterin oder den betroffenen
          Mieter, Einschränken, Löschen und Vernichten. Das Auslesen der Schreiben erfolgt
          automatisiert; eine automatisierte Entscheidung mit rechtlicher Wirkung gegenüber
          betroffenen Personen findet nicht statt, da jeder E-Mail-Entwurf vor dem Versand vom
          Verantwortlichen geprüft und freigegeben wird.
        </p>

        <h3>Kategorien betroffener Personen</h3>
        <ul className="legal-list">
          <li>Mieterinnen und Mieter sowie angegebene Fahrerinnen und Fahrer der Fahrzeuge des Verantwortlichen</li>
          <li>Beschäftigte des Verantwortlichen, die die Anwendung nutzen</li>
          <li>
            weitere in einem Schreiben genannte Personen, etwa Sachbearbeiterinnen und
            Sachbearbeiter der Behörde oder auf einem Beweisfoto abgebildete Personen
          </li>
        </ul>

        <h3>Kategorien personenbezogener Daten</h3>
        <ul className="legal-list">
          <li>
            <strong>Aus dem hochgeladenen Schreiben:</strong> die vollständigen Seitenbilder sowie
            die daraus ausgelesenen Angaben, insbesondere Kennzeichen, Aktenzeichen, Tatzeitpunkt,
            Tatort, Vorwurf und Tatbestandsnummer, Bußgeld, Gebühren und Auslagen, Punkte,
            Fahrverbot, Zahlungs- und Einspruchsfristen, ausstellende Behörde sowie Name und
            Anschrift der adressierten Person. Beweisfotos sind Teil des Seitenbildes.
          </li>
          <li>
            <strong>Mieterdaten aus der Vermietsoftware oder einem Import:</strong> Name,
            E-Mail-Adresse, Telefonnummer, Kennzeichen, Mietzeitraum, Buchungsreferenz,
            Buchungszeitpunkt, Fahrzeug und Übernahmestation.
          </li>
          <li>
            <strong>Kommunikationsdaten:</strong> Betreff, Inhalt, Empfängeradresse sowie
            Zeitpunkte von Erstellung, Bearbeitung, Freigabe, Versand und Zustellung der
            Fahrerbenennung.
          </li>
          <li>
            <strong>Nutzungsdaten der Beschäftigten des Verantwortlichen:</strong> Name,
            Benutzername, Passwortprüfwert, Sitzungs- und Anmeldezeitpunkte.
          </li>
        </ul>
        <p>
          Besondere Kategorien personenbezogener Daten nach Art. 9 DSGVO sind nicht Gegenstand des
          Auftrags. Enthält ein Schreiben im Einzelfall solche Angaben, werden sie weder gesondert
          ausgewertet noch als eigenes Merkmal gespeichert.
        </p>

        <h3>Dauer der Verarbeitung</h3>
        <p>
          Für die Laufzeit des Hauptvertrages, anschließend nach § 10 dieser Vereinbarung. Innerhalb
          der Laufzeit kann der Verantwortliche einzelne Vorgänge jederzeit löschen.
        </p>

        <h3>Ort der Verarbeitung</h3>
        <p>
          Deutschland. Einzige Ausnahme ist die automatisierte Auswertung der Seitenbilder, die
          nach Anlage 3 in den Vereinigten Staaten stattfindet.
        </p>
      </section>

      <section className="legal-section">
        <h2>Anlage 2 – Technische und organisatorische Maßnahmen</h2>
        <p>
          Maßnahmen nach Art. 32 DSGVO, Stand August 2026. Sie gelten für alle Bestandteile der
          Anwendung: die mobile App, den Konfigurator und die serverseitigen Dienste.
        </p>

        <h3>Vertraulichkeit</h3>
        <ul className="legal-list">
          <li>
            <strong>Zutrittskontrolle:</strong> Die Server stehen in einem Rechenzentrum des
            Hostinganbieters in Deutschland. Zutrittsschutz, Videoüberwachung und
            Besucherverwaltung obliegen diesem.
          </li>
          <li>
            <strong>Zugangskontrolle:</strong> Der Zugang zur Anwendung ist nur mit persönlichem
            Benutzerkonto und Passwort möglich. Passwörter werden nie im Klartext gespeichert,
            sondern ausschließlich als zweifach mit scrypt abgeleiteter Prüfwert mit
            benutzerindividuellem Salt und einem serverseitigen, nicht in der Datenbank
            hinterlegten Zusatzgeheimnis. Anmeldeversuche und weitere Zugriffe sind je Adresse
            mengenmäßig begrenzt.
          </li>
          <li>
            <strong>Sitzungen:</strong> Sitzungskennungen bestehen aus 32 zufälligen Bytes und
            werden nur als SHA3-256-Prüfwert gespeichert; die zugehörigen Angaben sind zusätzlich
            gegen unbemerkte Veränderung gesichert. Browsersitzungen laufen nach acht Stunden ab und
            können jederzeit zentral beendet werden.
          </li>
          <li>
            <strong>Zugriffskontrolle:</strong> Die internen Dienste vertrauen keiner Sitzung
            unmittelbar, sondern ausschließlich einem zentral ausgestellten, digital signierten und
            zehn Minuten gültigen Nachweis. Die Zugehörigkeit einer Anfrage zu einer Vermietung
            wird bei jedem Aufruf erneut aus der Datenbank gelesen, sodass ein Entzug sofort wirkt.
          </li>
          <li>
            <strong>Trennungskontrolle:</strong> Jeder Datensatz trägt die Kennung der Vermietung,
            zu der er gehört. Zusammengesetzte Fremdschlüssel stellen auf Datenbankebene sicher,
            dass ein Schreiben der einen Vermietung keiner Mieterin und keinem Mieter einer anderen
            zugeordnet werden kann.
          </li>
          <li>
            <strong>Pseudonymisierung:</strong> IP-Adressen der Website-Reichweitenmessung werden
            nur als gesalzener, gekürzter Prüfwert gespeichert.
          </li>
        </ul>

        <h3>Integrität</h3>
        <ul className="legal-list">
          <li>
            <strong>Weitergabekontrolle:</strong> Jede Verbindung zwischen App bzw. Browser und
            Server sowie zu Unterauftragsverarbeitern ist mit TLS verschlüsselt. Die
            serverinterne Kommunikation verlässt den Host nicht.
          </li>
          <li>
            <strong>Eingabekontrolle:</strong> Anlage, Änderung, Freigabe und Versand jedes
            Vorgangs werden mit Zeitstempel protokolliert. Bearbeitete und ursprünglich
            vorgeschlagene E-Mail-Texte werden getrennt aufbewahrt, sodass nachvollziehbar bleibt,
            was der Verantwortliche geändert hat.
          </li>
          <li>
            <strong>Auftragskontrolle:</strong> Unterauftragsverarbeiter werden vor dem Einsatz
            geprüft und vertraglich nach Art. 28 DSGVO verpflichtet.
          </li>
        </ul>

        <h3>Verfügbarkeit und Belastbarkeit</h3>
        <ul className="legal-list">
          <li>Regelmäßige Sicherung der Datenbank durch den Hostinganbieter.</li>
          <li>
            Automatischer Neustart und laufende Verfügbarkeitsprüfung aller Dienste; ein nicht
            erreichbarer Dienst wird automatisch aus der Auslieferung genommen.
          </li>
          <li>
            Fehlgeschlagene Auswertungen werden begrenzt und zeitlich gestaffelt wiederholt, sodass
            eine vorübergehende Störung keinen Vorgang verliert.
          </li>
          <li>Protokolldaten des Serverbetriebs werden nach sieben Tagen gelöscht.</li>
        </ul>

        <h3>Verfahren zur Überprüfung</h3>
        <ul className="legal-list">
          <li>
            Regelmäßige Aktualisierung der eingesetzten Software und Abgleich der Abhängigkeiten
            auf bekannte Schwachstellen.
          </li>
          <li>
            Automatisierte Tests der sicherheitsrelevanten Bestandteile, insbesondere der
            Anmeldung, der Nachweisprüfung und der Mandantentrennung, vor jeder Auslieferung.
          </li>
          <li>Überprüfung dieser Maßnahmen mindestens jährlich sowie bei wesentlichen Änderungen.</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>Anlage 3 – Unterauftragsverarbeiter</h2>
        <p>
          Genehmigt sind ausschließlich die folgenden Unterauftragsverarbeiter. Änderungen richten
          sich nach § 6.
        </p>
        <div className="legal-table-scroll">
          <table className="legal-table">
            <thead>
              <tr>
                <th>Unternehmen</th>
                <th>Leistung</th>
                <th>Ort der Verarbeitung</th>
              </tr>
            </thead>
            <tbody>
              {SUBPROCESSORS.map((sub) => (
                <tr key={sub.name}>
                  <td>
                    <strong>{sub.name}</strong>
                    <span className="legal-table-note">{sub.seat}</span>
                  </td>
                  <td>{sub.purpose}</td>
                  <td>{sub.location}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          An den Auswertungsdienst werden ausschließlich die Seitenbilder des jeweiligen Schreibens
          übermittelt. Mieterdaten, Kontaktdaten und die erzeugten E-Mails werden ihm nicht
          zugänglich gemacht. Die übermittelten Inhalte werden vertraglich nicht zum Training von
          Modellen verwendet.
        </p>
      </section>

      <section className="legal-section legal-facts">
        <h2>Stand</h2>
        <p>Stand dieser Vereinbarung: August 2026.</p>
        <p>
          Fragen zu dieser Vereinbarung beantworten wir unter{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </section>
    </article>
  );
}

/**
 * Allgemeine Geschäftsbedingungen, business customers only.
 *
 * Two clauses carry the weight here and neither is boilerplate. § 5 says Gatai
 * drafts and does not advise: the product prepares a Fahrerbenennung, which is
 * a legally significant act, so the line between a draft and a
 * Rechtsdienstleistung has to be drawn in the contract rather than left to be
 * inferred. § 14 follows from it - every message is released by the customer,
 * so a missed Frist is a consequence of that release not happening, and the
 * liability clause has to say so.
 *
 * No prices anywhere. `customer` carries a billing address and a first invoice
 * month, not a tariff, and the fees live in the individual offer.
 */
function Agb() {
  return (
    <article className="legal-page section-shell">
      <p className="eyebrow">Vertragsbedingungen für Vermietungen</p>
      {/* Soft hyphens for the same reason as the AVV heading, and the same
          `aria-label` so they stay out of the accessible name. */}
      <h1 aria-label="Allgemeine Geschäftsbedingungen">
        Allgemeine Geschäfts&shy;bedingungen
      </h1>
      <p className="legal-intro">
        Diese Bedingungen gelten für die Nutzung von Gatai durch Vermietungen und
        Fuhrparkbetreiber. Sie werden mit dem Angebot bzw. dem Einzelvertrag Bestandteil des
        Vertrages zwischen Ihnen und uns.
      </p>

      <section className="legal-section">
        <h2>§ 1 Geltungsbereich</h2>
        <p>
          Anbieter von Gatai ist {PROCESSOR.company}, Inhaber {PROCESSOR.owner},{" "}
          {PROCESSOR.street}, {PROCESSOR.city} (nachfolgend „Gatai“). Diese Allgemeinen
          Geschäftsbedingungen gelten für alle Verträge über die Nutzung von Gatai.
        </p>
        <p>
          Gatai richtet sich ausschließlich an Unternehmer im Sinne des § 14 BGB, an juristische
          Personen des öffentlichen Rechts und an öffentlich-rechtliche Sondervermögen. Verträge
          mit Verbrauchern kommen nicht zustande.
        </p>
        <p>
          Abweichende, entgegenstehende oder ergänzende Geschäftsbedingungen des Kunden werden
          nicht Vertragsbestandteil, auch wenn Gatai der Leistung in Kenntnis dieser Bedingungen
          widerspruchslos nachkommt.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 2 Leistungen von Gatai</h2>
        <p>Gatai stellt dem Kunden als Software as a Service zur Verfügung:</p>
        <ul className="legal-list">
          <li>
            eine mobile App mit Dokumentenupload für Bußgeldbescheide und weitere Halterschreiben
            sowie einer Übersicht der zugehörigen Nachrichten,
          </li>
          <li>
            die automatisierte Auslesung der hochgeladenen Schreiben und die Zuordnung des
            passenden Mietvorgangs anhand von Kennzeichen und Tatzeitpunkt,
          </li>
          <li>
            die Erstellung eines E-Mail-Entwurfs zur Fahrerbenennung sowie den Versand der vom
            Kunden freigegebenen E-Mail,
          </li>
          <li>
            eine Weboberfläche (Konfigurator) zur Verwaltung von Nachrichtenvorlagen und
            Mieterdaten.
          </li>
        </ul>
        <p>
          Der konkrete Leistungsumfang ergibt sich aus dem Angebot bzw. dem Einzelvertrag. Gatai
          wird als Software bereitgestellt; eine Erfolgs- oder Bearbeitungsgarantie für einzelne
          Vorgänge ist damit nicht verbunden.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 3 Vertragsschluss</h2>
        <p>
          Die Darstellung der Leistungen auf dieser Website ist kein bindendes Angebot. Der
          Vertrag kommt zustande, wenn der Kunde ein Angebot von Gatai in Textform annimmt oder
          einen Einzelvertrag unterzeichnet.
        </p>
        <p>
          Diese Bedingungen werden mit dem Vertragsschluss Bestandteil des Vertrages. Bei
          Widersprüchen gehen die Regelungen des Einzelvertrages diesen Bedingungen vor.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 4 Zugang und Benutzerkonten</h2>
        <p>
          Gatai richtet dem Kunden persönliche Benutzerkonten für seine Beschäftigten ein. Die
          Zugangsdaten sind vertraulich zu behandeln und dürfen nicht an Dritte weitergegeben
          werden. Der Kunde teilt Gatai unverzüglich mit, wenn der Verdacht besteht, dass ein
          Zugang unbefugt genutzt wird.
        </p>
        <p>
          Der Kunde ist für alle Handlungen verantwortlich, die über die ihm zugeordneten Konten
          vorgenommen werden. Er sorgt dafür, dass ausgeschiedene Beschäftigte keinen Zugang
          mehr haben, und teilt Gatai mit, welche Konten zu sperren sind.
        </p>
        <p>
          Gatai darf einen Zugang vorübergehend sperren, wenn konkrete Anhaltspunkte für eine
          missbräuchliche Nutzung oder eine Gefährdung der Systemsicherheit bestehen. Der Kunde
          wird darüber unverzüglich informiert.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 5 Keine Rechtsdienstleistung</h2>
        <p>
          Gatai erbringt keine Rechtsdienstleistung im Sinne des Rechtsdienstleistungsgesetzes
          und keine Rechtsberatung. Die Anwendung liest Schreiben aus und erzeugt daraus
          Textentwürfe; sie prüft weder die Rechtmäßigkeit eines Bescheids noch, ob eine
          Fahrerbenennung im Einzelfall geboten oder zweckmäßig ist.
        </p>
        <p>
          Jede von Gatai erzeugte Nachricht ist ein <strong>Entwurf</strong>. Sie wird erst
          versandt, nachdem der Kunde sie geprüft und freigegeben hat. Die inhaltliche Prüfung,
          die Entscheidung über den Versand und die Wahrung sämtlicher Fristen gegenüber
          Behörden liegen ausschließlich beim Kunden.
        </p>
        <p>
          Ausgelesene Angaben können unvollständig oder unzutreffend sein. Der Kunde gleicht sie
          vor der Freigabe mit dem Originalschreiben ab.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 6 Mitwirkungspflichten des Kunden</h2>
        <ul className="legal-list">
          <li>
            Der Kunde stellt die für die Zuordnung erforderlichen Mieterdaten vollständig,
            zutreffend und aktuell bereit.
          </li>
          <li>
            Der Kunde prüft jeden Entwurf vor der Freigabe und verantwortet die von ihm
            hinterlegten Nachrichtenvorlagen.
          </li>
          <li>
            Der Kunde überwacht die Fristen seiner Vorgänge eigenständig und verlässt sich dabei
            nicht allein auf die Anwendung.
          </li>
          <li>
            Der Kunde nutzt Gatai nur im Rahmen der Gesetze, insbesondere des Datenschutz- und
            Wettbewerbsrechts, und lädt keine Inhalte hoch, zu deren Verarbeitung er nicht
            berechtigt ist.
          </li>
          <li>
            Der Kunde benennt Gatai einen Ansprechpartner sowie eine E-Mail-Adresse für
            Mitteilungen nach diesen Bedingungen.
          </li>
        </ul>
        <p>
          Kommt der Kunde diesen Pflichten nicht nach, kann Gatai die betroffenen Leistungen
          insoweit nicht erbringen; hierdurch entstehende Verzögerungen gehen nicht zu Lasten
          von Gatai.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 7 Verfügbarkeit und Support</h2>
        <p>
          Gatai betreibt die Anwendung mit der Sorgfalt eines ordentlichen Kaufmanns, sagt aber
          keine bestimmte Verfügbarkeit zu. Zeiten planmäßiger Wartung, Störungen außerhalb des
          Einflussbereichs von Gatai sowie Ausfälle bei eingesetzten Dienstleistern gelten nicht
          als Nichtleistung. Planmäßige Wartungsarbeiten werden nach Möglichkeit angekündigt und
          außerhalb der üblichen Geschäftszeiten durchgeführt.
        </p>
        <p>
          Support leistet Gatai per E-Mail an <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>{" "}
          sowie telefonisch zu den in der Anwendung genannten Zeiten. Ein Anspruch auf eine
          bestimmte Reaktionszeit besteht nur, soweit er im Einzelvertrag vereinbart ist.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 8 Änderungen der Leistung</h2>
        <p>
          Gatai darf die Anwendung weiterentwickeln, insbesondere Funktionen ergänzen und die
          Bedienung ändern, solange der vertraglich geschuldete Leistungszweck erhalten bleibt.
        </p>
        <p>
          Wird eine für den Kunden wesentliche Funktion eingeschränkt oder eingestellt,
          informiert Gatai ihn mindestens zwei Monate vorher in Textform. Der Kunde kann den
          Vertrag in diesem Fall zum Wirksamwerden der Änderung außerordentlich kündigen.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 9 Vergütung und Zahlung</h2>
        <p>
          Die Höhe der Vergütung ergibt sich aus dem Angebot bzw. dem Einzelvertrag. Gatai wendet
          die Kleinunternehmerregelung nach § 19 UStG an; die genannten Preise sind Endpreise und
          ein Ausweis von Umsatzsteuer erfolgt nicht. Entfällt die Kleinunternehmerregelung, tritt
          die gesetzliche Umsatzsteuer zu den genannten Preisen hinzu.
        </p>
        <p>
          Die Vergütung wird monatlich im Voraus abgerechnet. Die Zahlung erfolgt per
          SEPA-Lastschrift über den von Gatai eingesetzten Zahlungsdienstleister; der Kunde erteilt
          das Mandat online über einen ihm übersandten Zahlungslink. Die Vorabankündigung erfolgt
          mit der Rechnung, spätestens einen Tag vor dem Einzug. Rechnungen werden elektronisch an
          die vom Kunden hinterlegte Rechnungsanschrift versandt; der Kunde hält diese aktuell.
        </p>
        <p>
          Die Abrechnung erfolgt ausschließlich per SEPA-Lastschrift. Scheitert ein Einzug oder
          liegt kein wirksames Mandat vor, ist die Vergütung nach Aufforderung innerhalb von 14
          Tagen auszugleichen. Die Kosten einer Rücklastschrift trägt der Kunde, soweit
          er sie zu vertreten hat; der Nachweis eines geringeren Schadens bleibt ihm unbenommen.
        </p>
        <p>
          Bei Zahlungsverzug gelten die gesetzlichen Regelungen. Gerät der Kunde mit einem nicht
          unerheblichen Teil der Vergütung länger als 30 Tage in Verzug, darf Gatai den Zugang
          nach vorheriger Ankündigung in Textform bis zum Ausgleich sperren; die Vergütungspflicht
          bleibt für diesen Zeitraum bestehen.
        </p>
        <p>
          Gatai darf die Vergütung mit einer Ankündigungsfrist von zwei Monaten zum Beginn eines
          Kalendermonats anpassen. Der Kunde kann den Vertrag in diesem Fall zum Wirksamwerden
          der Anpassung außerordentlich kündigen.
        </p>
        <p>
          Der Kunde kann nur mit unbestrittenen oder rechtskräftig festgestellten Forderungen
          aufrechnen.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 10 Laufzeit und Kündigung</h2>
        <p>
          Der Vertrag läuft auf unbestimmte Zeit und kann von beiden Seiten mit einer Frist von
          sieben Tagen zum Ende eines Kalendermonats in Textform gekündigt werden, soweit im
          Einzelvertrag nichts anderes vereinbart ist. Maßgeblich ist der Zugang der Kündigung.
        </p>
        <p>
          Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt unberührt. Ein
          wichtiger Grund liegt für Gatai insbesondere vor, wenn der Kunde die Anwendung trotz
          Abmahnung rechtswidrig nutzt.
        </p>
        <p>
          Nach Vertragsende stellt Gatai dem Kunden seine Daten auf Anforderung in einem
          gängigen Format zur Verfügung. Für die Löschung gilt § 10 des{" "}
          <a href="/vermietungen/AVV">Auftragsverarbeitungsvertrages</a>.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 11 Datenschutz</h2>
        <p>
          Der Kunde ist für die von ihm verarbeiteten personenbezogenen Daten Verantwortlicher im
          Sinne der DSGVO, Gatai ist insoweit Auftragsverarbeiter. Einzelheiten regelt der{" "}
          <a href="/vermietungen/AVV">Auftragsverarbeitungsvertrag</a>, der mit Vertragsschluss
          Bestandteil des Vertrages wird.
        </p>
        <p>
          Für die Verarbeitung beim Besuch dieser Website gilt die{" "}
          <a href="/Datenschutz">Datenschutzerklärung</a>.
        </p>
        <p>
          Der Kunde stellt sicher, dass er zur Übermittlung der von ihm eingestellten Daten an
          Gatai berechtigt ist und dass die betroffenen Personen entsprechend informiert wurden.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 12 Nutzungsrechte</h2>
        <p>
          Gatai räumt dem Kunden für die Vertragslaufzeit ein einfaches, nicht übertragbares und
          nicht unterlizenzierbares Recht ein, die Anwendung im vereinbarten Umfang für eigene
          Geschäftszwecke zu nutzen. Eine Weitergabe an Dritte, eine Vermietung sowie das
          Zurückentwickeln der Software sind nicht gestattet.
        </p>
        <p>
          Die vom Kunden eingestellten Inhalte, insbesondere Nachrichtenvorlagen, Mieterdaten und
          hochgeladene Schreiben, bleiben seine. Gatai nutzt sie ausschließlich zur
          Vertragserfüllung und nicht zum Training eigener oder fremder Modelle.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 13 Vertraulichkeit</h2>
        <p>
          Beide Seiten behandeln Informationen der jeweils anderen Seite, die als vertraulich
          gekennzeichnet oder ihrer Natur nach vertraulich sind, vertraulich und nutzen sie nur
          zur Vertragserfüllung. Diese Pflicht besteht für drei Jahre nach Vertragsende fort.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 14 Haftung</h2>
        <p>
          Gatai haftet unbeschränkt bei Vorsatz und grober Fahrlässigkeit, bei der Verletzung von
          Leben, Körper oder Gesundheit, nach dem Produkthaftungsgesetz sowie im Umfang einer
          übernommenen Garantie.
        </p>
        <p>
          Bei leicht fahrlässiger Verletzung einer Pflicht, deren Erfüllung die ordnungsgemäße
          Durchführung des Vertrages überhaupt erst ermöglicht und auf deren Einhaltung der Kunde
          regelmäßig vertrauen darf, haftet Gatai der Höhe nach begrenzt auf den vertragstypischen,
          vorhersehbaren Schaden. Im Übrigen ist die Haftung ausgeschlossen.
        </p>
        <p>
          Ausgeschlossen ist insbesondere die Haftung für Schäden, die daraus entstehen, dass der
          Kunde einen Entwurf nicht, nicht rechtzeitig oder mit unzutreffendem Inhalt freigibt,
          sowie für Folgen versäumter Fristen gegenüber Behörden. Dies folgt aus § 5: die Prüfung,
          die Freigabe und die Fristenwahrung liegen beim Kunden.
        </p>
        <p>
          Für den Verlust von Daten haftet Gatai nur in Höhe des Aufwands, der bei ordnungsgemäßer
          und regelmäßiger Datensicherung durch den Kunden zur Wiederherstellung erforderlich
          gewesen wäre.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 15 Freistellung</h2>
        <p>
          Der Kunde stellt Gatai von Ansprüchen Dritter frei, die daraus entstehen, dass er die
          Anwendung rechtswidrig nutzt oder Daten und Inhalte einstellt, zu deren Verarbeitung er
          nicht berechtigt ist. Die Freistellung umfasst die erforderlichen Kosten der
          Rechtsverteidigung.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 16 Änderungen dieser Bedingungen</h2>
        <p>
          Gatai darf diese Bedingungen ändern, soweit dies zur Anpassung an geänderte
          Rechtslagen, an die technische Entwicklung oder an einen erweiterten Leistungsumfang
          erforderlich ist und der Kunde dadurch nicht unangemessen benachteiligt wird.
        </p>
        <p>
          Die Änderung wird dem Kunden mindestens zwei Monate vor ihrem Wirksamwerden in Textform
          mitgeteilt. Widerspricht er nicht bis zum Wirksamwerden, gilt die Änderung als
          angenommen; hierauf weist Gatai in der Mitteilung gesondert hin. Widerspricht der Kunde,
          kann jede Seite den Vertrag zum Wirksamwerden der Änderung kündigen.
        </p>
      </section>

      <section className="legal-section">
        <h2>§ 17 Schlussbestimmungen</h2>
        <p>
          Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des UN-Kaufrechts.
          Ausschließlicher Gerichtsstand für alle Streitigkeiten aus diesem Vertrag ist Berlin,
          soweit der Kunde Kaufmann, juristische Person des öffentlichen Rechts oder
          öffentlich-rechtliches Sondervermögen ist.
        </p>
        <p>
          Änderungen und Ergänzungen des Vertrages bedürfen der Textform. Nebenabreden bestehen
          nicht.
        </p>
        <p>
          Sollte eine Bestimmung unwirksam sein oder werden, bleibt die Wirksamkeit der übrigen
          Bestimmungen unberührt.
        </p>
      </section>

      <section className="legal-section legal-facts">
        <h2>Stand</h2>
        <p>Stand dieser Bedingungen: August 2026.</p>
        <address>
          <strong>{PROCESSOR.company}</strong>
          <span>Inhaber: {PROCESSOR.owner}</span>
          <span>{PROCESSOR.street}</span>
          <span>{PROCESSOR.city}</span>
          <span>
            E-Mail: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </span>
        </address>
      </section>
    </article>
  );
}

const BODIES: Record<LegalPageKind, () => React.JSX.Element> = {
  impressum: Impressum,
  datenschutz: Datenschutz,
  avv: Avv,
  agb: Agb,
};

function LegalPage({ page }: LegalPageProps) {
  const { title, path, noindex } = PAGES[page];
  const Body = BODIES[page];

  React.useEffect(() => {
    const previousTitle = document.title;
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const previousCanonical = canonical?.href;
    // Rewritten rather than added alongside: index.html already ships a robots
    // tag, and two of them leave the precedence to the crawler.
    const robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const previousRobots = robots?.content;

    document.title = `${title} | Gatai`;
    canonical?.setAttribute("href", `https://gatai.de${path}`);
    if (noindex) robots?.setAttribute("content", "noindex,nofollow");

    return () => {
      document.title = previousTitle;
      if (canonical && previousCanonical) canonical.href = previousCanonical;
      if (robots && previousRobots !== undefined) robots.content = previousRobots;
    };
  }, [title, path, noindex]);

  return (
    <div className="app legal-app">
      <LegalHeader />
      <main className="legal-main">
        <Body />
      </main>
      <SiteFooter />
    </div>
  );
}

export default LegalPage;
